import { PaymentMethod, SavePaymentMethodRequest } from '../types/billing';

const API_BASE_URL = import.meta.env.VITE_BASE_API || 'https://ycx2wdyct1.execute-api.us-east-2.amazonaws.com/dev';

export interface SavePaymentMethodResponse {
  success: boolean;
  paymentMethod: PaymentMethod;
  error?: string;
}

/**
 * Save a payment method to MongoDB via Lambda function
 */
export const savePaymentMethod = async (
  request: SavePaymentMethodRequest
): Promise<SavePaymentMethodResponse> => {
  try {
    const url = `${API_BASE_URL}/payment`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify(request),
      // Add credentials if needed for CORS
      // credentials: 'include',
    });


    // Check for CORS errors
    if (response.status === 0 || response.type === 'opaque') {
      const corsError = 'CORS error: API Gateway is not allowing requests from this origin. Check CORS configuration.';
      console.error('❌', corsError);
      throw new Error(corsError);
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        // If response is not JSON, check if it's a CORS issue
        if (response.status === 405) {
          throw new Error('Method not allowed. Ensure POST method is enabled in API Gateway.');
        }
        if (response.status === 403) {
          throw new Error('Forbidden. Check API Gateway CORS configuration and method permissions.');
        }
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      
      const errorMessage = errorData.error || errorData.message || `Failed to save payment method: ${response.status}`;
      console.error('❌ Error response:', errorData);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // Handle different response formats
    if (data.paymentMethod) {
      return {
        success: true,
        paymentMethod: data.paymentMethod,
      };
    }
    
    // If response is the payment method directly
    if (data.id || data.stripePaymentMethodId) {
      return {
        success: true,
        paymentMethod: data as PaymentMethod,
      };
    }
    
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('❌ Error saving payment method:', error);
    return {
      success: false,
      paymentMethod: {} as PaymentMethod,
      error: error instanceof Error ? error.message : 'Failed to save payment method',
    };
  }
};

/**
 * Fetch payment methods for a practice or user
 */
export const fetchPaymentMethods = async (
  practiceId?: string,
  userId?: string
): Promise<PaymentMethod[]> => {
  try {
    const params = new URLSearchParams();
    if (practiceId) params.append('practiceId', practiceId);
    if (userId) params.append('userId', userId);

    const url = `${API_BASE_URL}/payment?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${getAuthToken()}`,
      },
      // Add credentials if needed for CORS
      // credentials: 'include',
    });


    // Check for CORS errors
    if (response.status === 0 || response.type === 'opaque') {
      const corsError = 'CORS error: API Gateway is not allowing requests from this origin. Check CORS configuration.';
      console.error('❌', corsError);
      throw new Error(corsError);
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        if (response.status === 405) {
          throw new Error('Method not allowed. Ensure GET method is enabled in API Gateway.');
        }
        if (response.status === 403) {
          throw new Error('Forbidden. Check API Gateway CORS configuration and method permissions.');
        }
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      
      const errorMessage = errorData.error || errorData.message || `Failed to fetch payment methods: ${response.status}`;
      console.error('❌ Error response:', errorData);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // Handle different response formats
    if (Array.isArray(data)) {
      return data;
    }
    if (data.paymentMethods && Array.isArray(data.paymentMethods)) {
      return data.paymentMethods;
    }
    
    console.warn('⚠️ Unexpected response format:', data);
    return [];
  } catch (error) {
    console.error('❌ Error fetching payment methods:', error);
    // Re-throw the error so Redux can handle it
    throw error;
  }
};

/**
 * Delete a payment method
 */
export const deletePaymentMethod = async (
  paymentMethodId: string
): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/${paymentMethodId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    return response.ok;
  } catch (error) {
    console.error('Error deleting payment method:', error);
    return false;
  }
};

/**
 * Set a payment method as default
 */
export const setDefaultPaymentMethod = async (
  paymentMethodId: string,
  practiceId?: string,
  userId?: string
): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/${paymentMethodId}/set-default`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({
        practiceId,
        userId,
      }),
    });

    return response.ok;
  } catch (error) {
    console.error('Error setting default payment method:', error);
    return false;
  }
};
